// web/src/shell/CookieConsent.tsx
//
// Minimal GDPR-style consent banner for telemetry & (future) analytics
// cookies. Auth/session cookies are strictly necessary and are NOT gated
// behind this banner — the app cannot function without them.
//
// State:
//   "unknown"  — first visit, banner is shown
//   "accepted" — Sentry + future analytics may run
//   "declined" — non-essential telemetry stays off
//
// Stored as plain string in localStorage at `tapelens.consent`. The
// helpers below are intentionally framework-free so non-React modules
// (e.g. main.tsx, main.marketing.tsx, observability/sentry.ts) can read
// the consent state at boot without spinning up React.
//
// Reopening the banner from elsewhere: dispatch
// `window.dispatchEvent(new Event("cookieconsent:open"))`. This is what
// the "Cookie preferences" link in PublicFooter does.
import { useEffect, useState } from "react";
import { Cookie } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";

export type ConsentValue = "unknown" | "accepted" | "declined";

const STORAGE_KEY = "tapelens.consent";
const OPEN_EVENT = "cookieconsent:open";

export function getConsent(): ConsentValue {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === "accepted" || raw === "declined") return raw;
  } catch {
    /* localStorage can throw in some sandboxed contexts */
  }
  return "unknown";
}

export function setConsent(value: Exclude<ConsentValue, "unknown">): void {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    /* silently ignore — user can choose again next visit */
  }
}

export function clearConsent(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function openCookieConsent(): void {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

interface Props {
  /**
   * Called after the user makes a choice. Receives the new consent value.
   * The caller can use this to initialise / shut down telemetry without
   * a page reload (Sentry, future analytics).
   */
  onChange?: (value: Exclude<ConsentValue, "unknown">) => void;
  /** Privacy-policy link target. Defaults to the public legal route. */
  privacyHref?: string;
}

export function CookieConsent({ onChange, privacyHref = "/legal/privacy" }: Props) {
  const { t } = useTranslation("common");
  // The banner is visible when:
  //   - the user has not yet made a choice ("unknown"), OR
  //   - someone dispatched the OPEN_EVENT to re-prompt (e.g. footer link).
  const [visible, setVisible] = useState<boolean>(false);

  useEffect(() => {
    setVisible(getConsent() === "unknown");
    function handler() {
      setVisible(true);
    }
    window.addEventListener(OPEN_EVENT, handler);
    return () => window.removeEventListener(OPEN_EVENT, handler);
  }, []);

  if (!visible) return null;

  function decide(value: Exclude<ConsentValue, "unknown">) {
    setConsent(value);
    setVisible(false);
    onChange?.(value);
  }

  return (
    <div
      role="region"
      aria-label={t("cookieConsent.regionLabel")}
      aria-live="polite"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-card/95 px-4 py-4 shadow-lg backdrop-blur"
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
        <div className="flex flex-1 items-start gap-3">
          <Cookie className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
          <p className="text-sm text-foreground">
            <Trans
              i18nKey="common:cookieConsent.body"
              components={{
                strong: <strong className="font-medium" />,
                privacy: (
                  <a href={privacyHref} className="underline hover:no-underline" />
                ),
              }}
            />
          </p>
        </div>
        <div className="flex flex-shrink-0 gap-2 sm:justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => decide("declined")}
          >
            {t("cookieConsent.reject")}
          </Button>
          <Button size="sm" onClick={() => decide("accepted")}>
            {t("cookieConsent.accept")}
          </Button>
        </div>
      </div>
    </div>
  );
}
